import { Box3, Group, Object3D, Vector3 } from 'three';

import { removeSelectionGroups } from '../ai-tools/selection-label.helper';
import {
	createExploreMarkerSprite,
	EXPLORE_MARKER_CATEGORIES,
	EXPLORE_MARKER_GROUP_TYPE,
	EXPLORE_MARKER_SIZE,
	EXPLORE_MARKER_TYPE,
	ExploreMarkerCategory
} from './explore-marker.helper';

/** How far out from the anchor's face a bubble floats. */
const OUT_IN = 12;

export type ExploreMarker = {
	/** The domain id of the thing the bubble opens */
	targetId: string;
	category: ExploreMarkerCategory;
	label: string;
};

export type ShowExploreMarkersParams = {
	islands: Array<Object3D>;
	/** Which kinds to draw. Defaults to all three. */
	categories?: Array<ExploreMarkerCategory>;
};

type Anchor = { object: Object3D; targetId: string; category: ExploreMarkerCategory; label: string };

/** Out towards the viewer, at the height the category reads best. */
const markerPosition = (anchor: Object3D, category: ExploreMarkerCategory): Vector3 => {
	const box = new Box3().setFromObject(anchor);
	const position = box.getCenter(new Vector3());

	const outward = new Vector3();
	anchor.getWorldDirection(outward);
	if (outward.lengthSq() < 1e-6) outward.set(0, 0, 1);
	position.add(outward.multiplyScalar(OUT_IN));

	// A countertop bubble floats over the slab; a surround bubble sits low on the island face so it
	// cannot land on the appliances in front of it.
	if (category === 'countertop') position.y = box.max.y + (EXPLORE_MARKER_SIZE * 0.5);
	if (category === 'surround') position.y = box.min.y + ((box.max.y - box.min.y) * 0.3);

	return position;
};

/** One anchor per domain object, first mesh wins — a .dae appliance is many meshes under one id. */
const collectAnchors = (islands: Array<Object3D>, categories: Array<ExploreMarkerCategory>): Array<Anchor> => {
	const anchors = new Map<string, Anchor>();
	const wanted = new Set(categories);

	for (const islandGroup of islands) {
		const { id: islandId } = islandGroup.userData ?? {};
		if (wanted.has('surround') && islandId) {
			anchors.set(String(islandId), { object: islandGroup, targetId: String(islandId), category: 'surround', label: 'Island finish' });
		}

		islandGroup.traverse((child) => {
			const { id, type, sku, countertopId } = child.userData ?? {};
			if (!id) return;
			if (type === 'appliance' && wanted.has('appliance')) {
				const targetId = String(id);
				if (!anchors.has(targetId)) anchors.set(targetId, { object: child, targetId, category: 'appliance', label: String(sku ?? 'Appliance') });
			}
			if (type === 'countertop' && wanted.has('countertop')) {
				const targetId = String(countertopId ?? id);
				if (!anchors.has(targetId)) anchors.set(targetId, { object: child, targetId, category: 'countertop', label: 'Countertop' });
			}
		});
	}

	return [...anchors.values()];
};

/**
 * Draw a `+` bubble on every surround, appliance and countertop, replacing the previous set.
 * Clicking one publishes through the renderer's normal object-click bridge, carrying the
 * category and target id in `payload.data`, so the consuming app decides what it opens.
 * Returns what was drawn so the controller can publish it.
 */
export const showExploreMarkersCfn = (params: ShowExploreMarkersParams): Array<ExploreMarker> => {
	const { islands, categories = EXPLORE_MARKER_CATEGORIES } = params;
	if (!islands || islands.length === 0) return [];

	const objectHolder = islands[0].parent as Group | null;
	if (!objectHolder) return [];

	removeSelectionGroups(objectHolder, [EXPLORE_MARKER_GROUP_TYPE]);

	const anchors = collectAnchors(islands, categories);
	if (anchors.length === 0) return [];

	const markersGroup = new Group();
	markersGroup.userData = { type: EXPLORE_MARKER_GROUP_TYPE };
	const drawn: Array<ExploreMarker> = [];

	for (const { object, targetId, category, label } of anchors) {
		const sprite = createExploreMarkerSprite(category);
		sprite.position.copy(markerPosition(object, category));
		// `eventData` rides along into the click payload's `data` — see Render3DKitchen's bridge.
		sprite.userData = { id: targetId, type: EXPLORE_MARKER_TYPE, label, eventData: { category, targetId, label } };

		markersGroup.add(sprite);
		drawn.push({ targetId, category, label });
	}

	objectHolder.add(markersGroup);
	return drawn;
};

/** Take every bubble off the scene. */
export const hideExploreMarkersCfn = (islands: Array<Object3D>): void => {
	const objectHolder = islands?.[0]?.parent as Group | null;
	if (objectHolder) removeSelectionGroups(objectHolder, [EXPLORE_MARKER_GROUP_TYPE]);
};
